import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import { unsubscribeByToken, resubscribeByToken } from '../../api/notificationApi';
import styles from './UnsubscribePage.module.css';

/**
 * 알림 종류별 이름. 메일 링크의 type과 서버 응답의 field가 이 키로 온다.
 * 뒤에 '메일'을 붙여 쓰므로 이름 자체에는 넣지 않는다.
 * 모르는 값이면 '알림'이라고만 말한다 — 엉뚱한 이름을 들이대는 것보다 낫다.
 */
const LABELS = {
  castingNotification: '새로운 캐스팅 공고 알림',
  messageNotification: '메시지 알림',
  marketingNotification: '마케팅',
  emailNotification: '이메일 알림',
};

const labelOf = (field) => LABELS[field] || '알림';

/**
 * 메일 수신 거부 페이지.
 *
 * 메일 푸터의 '이 알림 그만 받기' 링크가 여기로 온다. 로그인 없이 토큰만으로 처리한다 —
 * 메일을 여는 시점에 로그인돼 있는 경우가 드물고, 로그인을 요구하면 그냥 스팸 신고를 누른다.
 *
 * 페이지를 열자마자 끄지 않고 버튼을 한 번 누르게 한다. 메일 서비스와 보안 제품이 본문의
 * 링크를 미리 열어보기 때문에, 열리기만 해도 꺼진다면 누르지도 않은 알림이 꺼진다.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const typeFromLink = params.get('type');

  const [status, setStatus] = useState('idle'); // idle | saving | done | undone | error
  const [field, setField] = useState(typeFromLink);

  const run = async (action, nextStatus) => {
    setStatus('saving');
    try {
      const res = await action(token);
      // 어느 알림을 껐는지는 서버 응답이 정답이다. 링크의 type은 그전까지 쓰는 표시용 값이다.
      if (res?.field) setField(res.field);
      setStatus(nextStatus);
    } catch {
      setStatus('error');
    }
  };

  const saving = status === 'saving';
  const name = labelOf(field);

  return (
    <PageLayout>
      <div className={styles.page}>
        <div className={styles.card}>
          {!token ? (
            <>
              <h1 className={styles.title}>링크가 올바르지 않습니다</h1>
              <p className={styles.body}>
                메일에 담긴 주소를 그대로 열어주세요. 주소가 잘린 경우에도 이 화면이 보일 수 있습니다.
                <br />
                로그인 후 알림 설정에서도 언제든 끄실 수 있습니다.
              </p>
              <Link className={styles.linkBtn} to="/talent/settings">알림 설정으로 가기</Link>
            </>
          ) : status === 'done' ? (
            <>
              <h1 className={styles.title}>수신 거부됐습니다</h1>
              <p className={styles.body}>
                앞으로 <strong>{name}</strong> 메일을 보내지 않습니다.
                <br />
                실수로 누르셨다면 아래에서 되돌릴 수 있습니다.
              </p>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={saving}
                  onClick={() => run(resubscribeByToken, 'undone')}
                >
                  다시 받기
                </button>
                <Link className={styles.linkBtn} to="/">캐스타 홈으로</Link>
              </div>
            </>
          ) : status === 'undone' ? (
            <>
              <h1 className={styles.title}>다시 받도록 되돌렸습니다</h1>
              <p className={styles.body}>
                <strong>{name}</strong> 메일을 다시 보내드립니다.
              </p>
              <Link className={styles.linkBtn} to="/">캐스타 홈으로</Link>
            </>
          ) : (
            <>
              <h1 className={styles.title}>{name} 메일을 그만 받으시겠어요?</h1>
              <p className={styles.body}>
                아래 버튼을 누르면 이 알림 메일을 더 이상 보내지 않습니다.
                <br />
                지원 현황처럼 서비스 이용에 필요한 안내는 계속 받으실 수 있습니다.
              </p>
              {status === 'error' && (
                <p className={styles.error} role="alert">
                  처리하지 못했습니다. 잠시 후 다시 시도하시거나, 로그인 후 알림 설정에서 꺼주세요.
                </p>
              )}
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={saving}
                  onClick={() => run(unsubscribeByToken, 'done')}
                >
                  {saving ? '처리 중…' : '수신 거부'}
                </button>
                <Link className={styles.linkBtn} to="/">그냥 계속 받기</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
